import type { TastingStop } from '../types';
import { imageUrl } from './images';

export const TASTING_STOPS: TastingStop[] = [
  {
    id: 'texas-hill-country-olive-co',
    type: 'tasting-stop',
    verified: true,
    lastVerified: '2026-04-22',
    region: 'dripping-springs-fitzhugh',
    name: 'Texas Hill Country Olive Co.',
    description:
      'The flagship public-facing olive destination in Dripping Springs: guided olive-oil and balsamic tastings, orchard context, a retail shop, and an on-site bistro all in one stop.',
    address: '2530 W Fitzhugh Rd',
    city: 'Dripping Springs',
    state: 'TX',
    lat: 30.2362,
    lng: -98.1083,
    image: imageUrl('olive-orchard-field'),
    hours: 'Property open daily; guided tastings run daily with extra Friday-Sunday slots.',
    website: 'https://texashillcountryoliveco.com/pages/plan-your-visit',
    phone: '(512) 607-6512',
    highlights: ['Guided tasting flights', 'Organic orchard story', 'On-site bistro', 'Retail shop'],
    varietals: ['Blend', 'Arbequina'],
    experiences: ['Guided Tasting', 'Orchard Tour'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    story:
      'Texas Hill Country Olive Co. is the most tourism-ready olive stop in Texas, combining production education with a polished tasting-room format that actually works for travelers.',
    pairingNotes: ['Fresh focaccia', 'Citrus-forward oils', 'Balsamic comparisons'],
    costRange: '$$',
  },
  {
    id: 'camp-lucy-olive-oil-tasting',
    type: 'tasting-stop',
    verified: true,
    lastVerified: '2026-04-22',
    region: 'dripping-springs-fitzhugh',
    name: 'Camp Lucy Olive Oil Tasting',
    description:
      'A resort-based olive-oil class in Dripping Springs that teaches guests to taste like a sommelier, with small bites and a glass of Camp Lucy wine.',
    address: '3509 Creek Rd',
    city: 'Dripping Springs',
    state: 'TX',
    lat: 30.2236,
    lng: -98.0538,
    image: imageUrl('olive-tasting-plate'),
    hours: 'Scheduled as a bookable daily activity for lodging guests and day visitors.',
    website: 'https://www.camplucy.com/onsite-programming/olive-oil-tasting/',
    phone: '(512) 894-4400',
    highlights: ['Sommelier-style class', 'Wine pairing', 'Public day-visitor access', 'Luxury setting'],
    varietals: ['Blend'],
    experiences: ['Sommelier Class', 'Guided Tasting'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    story:
      'Camp Lucy is not an orchard, but it earns its place because it turns olive oil into a polished hospitality experience rather than a shelf product.',
    pairingNotes: ['Small bites', 'Camp Lucy wine', 'Sensory tasting language'],
    costRange: '$$$',
  },
  {
    id: 'gruene-olive-tasting-room',
    type: 'tasting-stop',
    verified: true,
    lastVerified: '2026-04-22',
    region: 'new-braunfels-gruene',
    name: 'The Gruene Olive Tasting Room',
    description:
      'A reliable retail-tasting stop in historic Gruene with olive oils and balsamics on tap, easy sampling, and a location that fits naturally into a river-town weekend.',
    address: '1641 Hunter Rd',
    city: 'New Braunfels',
    state: 'TX',
    lat: 29.7388,
    lng: -98.1591,
    image: imageUrl('olive-oil-shelf'),
    hours: 'Open daily, typically 10am-6pm.',
    website: 'https://www.grueneolive.com/pages/contact-us',
    phone: '(830) 627-9087',
    highlights: ['Historic Gruene location', 'On-tap sampling', 'Large flavor range', 'Easy walk-in visit'],
    varietals: ['Blend'],
    experiences: ['Retail Sampling'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    story:
      'This is the easiest olive-oil stop to fold into a casual New Braunfels or Gruene stay because the tasting is straightforward, central, and built for browsing.',
    pairingNotes: ['Infused oils', 'Balsamic sampling', 'Giftable pantry finds'],
    costRange: '$',
  },
  {
    id: 'central-texas-olive-ranch',
    type: 'tasting-stop',
    verified: true,
    lastVerified: '2026-04-22',
    region: 'central-texas-detours',
    name: 'Central Texas Olive Ranch',
    description:
      'A working Georgetown producer with a public Saturday tasting-room presence and a free guided-tour request form, making it one of the clearest orchard detours north of Austin.',
    address: '5251 FM 972',
    city: 'Georgetown',
    state: 'TX',
    lat: 30.7077,
    lng: -97.5351,
    image: imageUrl('olive-harvest'),
    hours: 'Open Saturdays; tours available by request through the ranch.',
    website: 'https://www.txolive.com/',
    phone: '(512) 744-5167',
    highlights: ['Free guided tours', 'Saturday tasting room', 'Working ranch context', 'North-of-Austin detour'],
    varietals: ['Blend', 'Arbequina'],
    experiences: ['Guided Tasting', 'Orchard Tour'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    story:
      'Central Texas Olive Ranch feels less polished than Dripping Springs and more producer-led, which is exactly why it works as an orchard detour for repeat visitors.',
    pairingNotes: ['Fresh bread', 'Robust finishing oils', 'Producer conversation'],
    costRange: '$$',
  },
  {
    id: 'dells-favorite-texas-olive-ranch',
    type: 'tasting-stop',
    verified: true,
    lastVerified: '2026-04-22',
    region: 'central-texas-detours',
    name: "Dell's Favorite Texas Olive Ranch",
    description:
      'A small Elgin orchard and producer with its own Texas-grown EVOO, best treated as a lower-key farm detour rather than a fully built-out tourism stop.',
    address: '186 Youngs Prairie Rd',
    city: 'Elgin',
    state: 'TX',
    lat: 30.3296,
    lng: -97.3572,
    image: imageUrl('single-olive-branch'),
    hours: 'Small-orchard visit; confirm current visit and purchase availability before driving out.',
    website: 'https://www.dellsfavorite.com/',
    phone: '(512) 229-8443',
    highlights: ['Small orchard scale', 'Texas-grown EVOO', 'Chef-founded story', 'Best as phone-first detour'],
    varietals: ['Arbequina', 'Arbosana', 'Blend'],
    experiences: ['Farm Pickup'],
    tastingAvailable: true,
    visitorPolicy: 'by-appointment',
    story:
      'Dell’s Favorite is included because it is real and documented, but it is positioned honestly as a phone-first producer visit rather than a polished tasting campus.',
    pairingNotes: ['Simple dipping service', 'Cooking-forward oils', 'Small-batch producer talk'],
    costRange: '$$',
  },
  {
    id: 'santa-fe-olive-oil-balsamic',
    type: 'tasting-stop',
    verified: true,
    lastVerified: '2026-04-22',
    region: 'santa-fe-plaza',
    name: 'Santa Fe Olive Oil & Balsamic Co.',
    description:
      'A downtown Santa Fe tasting room on Don Gaspar Avenue with daily tastings, a wide premium EVOO range, and a strong New Mexico flavor angle in its infused oils.',
    address: '116 Don Gaspar Ave',
    city: 'Santa Fe',
    state: 'NM',
    lat: 35.6868,
    lng: -105.9397,
    image: imageUrl('olive-tasting-plate'),
    hours: 'Open daily, typically 10am-6pm.',
    website: 'https://santafeoliveoil.com/pages/tasting-room',
    phone: '(505) 992-1601',
    highlights: ['Santa Fe Plaza location', 'Daily tastings', 'Certified staff', 'New Mexico chile infusions'],
    varietals: ['Arbequina', 'Coratina', 'Picual', 'Blend'],
    experiences: ['Retail Sampling', 'Guided Tasting'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    story:
      'Santa Fe Olive Oil earns its place because it turns olive tasting into a walkable downtown experience, with enough range and staff guidance to make Santa Fe feel like a real destination stop.',
    pairingNotes: ['Jalapeno oil', 'Mesquite smoked oil', 'Balsamic comparisons'],
    costRange: '$$',
  },
  {
    id: 'abq-olive-oil-company',
    type: 'tasting-stop',
    verified: true,
    lastVerified: '2026-04-22',
    region: 'albuquerque-corrales',
    name: 'ABQ Olive Oil Company',
    description:
      'A Corrales-area tap-room style olive shop offering ultra-premium oils, balsamics, occasional classes, and private tasting-style experiences in the Albuquerque metro.',
    address: '10700 Corrales Rd NW, Suite E',
    city: 'Albuquerque',
    state: 'NM',
    lat: 35.2101,
    lng: -106.6062,
    image: imageUrl('olive-bottle-counter'),
    hours: 'Store open Monday-Saturday; classes and tasting events announced separately.',
    website: 'https://abqoliveoilco.com/',
    phone: '(505) 899-9293',
    highlights: ['Tap-room style sampling', 'Classes and home tastings', 'Corrales location', 'Ultra-premium oils'],
    varietals: ['Blend', 'Picual', 'Arbequina'],
    experiences: ['Retail Sampling', 'Guided Tasting'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    story:
      'ABQ Olive Oil Company is the more practical Albuquerque-area stop: less polished than Santa Fe, but useful for road trippers who want a solid tasting-room visit without committing to a resort town detour.',
    pairingNotes: ['Citrus oils', 'Cooking demos', 'Balsamic flight browsing'],
    costRange: '$',
  },
  {
    id: 'queen-creek-olive-mill',
    type: 'tasting-stop',
    verified: true,
    lastVerified: '2026-04-22',
    region: 'queen-creek-phoenix',
    name: 'Queen Creek Olive Mill',
    description:
      'Arizona’s signature olive destination and the strongest agritourism stop in the expansion, with daily Olive Oil 101 classes, marketplace browsing, and a full mill-centered visitor experience.',
    address: '25062 S Meridian Rd',
    city: 'Queen Creek',
    state: 'AZ',
    lat: 33.2171,
    lng: -111.5811,
    image: imageUrl('olive-hills'),
    hours: 'Open daily; Olive Oil 101 classes offered seven days a week with multiple daily time slots.',
    website: 'https://www.queencreekolivemill.com/pages/olive-oil-101',
    highlights: ['Working mill', 'Daily Olive Oil 101', 'Marketplace', 'Large outdoor grove'],
    varietals: ['Blend', 'Arbequina', 'Koroneiki'],
    experiences: ['Guided Tasting', 'Orchard Tour'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    story:
      'Queen Creek Olive Mill is the strongest Arizona stop because it offers a real public mill visit, daily classes, and enough activity on site to make it feel like a destination rather than a quick retail stop.',
    pairingNotes: ['Olive Oil 101 flight', 'Marketplace pantry haul', 'Fresh-mill context'],
    costRange: '$$',
  },
  {
    id: 'sonoran-desert-olive-oil-company',
    type: 'tasting-stop',
    verified: true,
    lastVerified: '2026-04-22',
    region: 'queen-creek-phoenix',
    name: 'Sonoran Desert Olive Oil Company',
    description:
      'A North Phoenix tasting room built around fresh-pressed oils, balsamics, and hands-on sampling, useful as the metro complement to the more destination-style Queen Creek mill.',
    address: '5350 E High St #113',
    city: 'Phoenix',
    state: 'AZ',
    lat: 33.6769,
    lng: -111.9645,
    image: imageUrl('olive-branch-close'),
    hours: 'Open limited weekly hours, generally Wednesday-Saturday with shorter weekend service.',
    website: 'https://www.sonorandesertoliveoil.com/contact',
    phone: '(480) 656-9076',
    highlights: ['Hands-on tasting room', 'High Street location', 'Fresh-pressed positioning', 'Metro convenience'],
    varietals: ['Blend'],
    experiences: ['Retail Sampling'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    story:
      'Sonoran Desert Olive Oil Company gives Phoenix-area travelers an easier tasting-room option, especially if they want a lighter metro stop in addition to the bigger Queen Creek mill visit.',
    pairingNotes: ['Fresh-pressed flights', 'Infused oils', 'Balsamic shelf browsing'],
    costRange: '$',
  },
];
